import React, { type HTMLAttributes } from 'react';
import styled from 'styled-components';
import { filterXSS } from 'xss';
import { useOffersContext } from '@/app/components/hotel-detail/OffersProvider';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { useTranslation } from '@/app/components/translations/Translation';
import { formatCheckinTime, getHotelCheckinCheckoutDates } from '@/app/components/hotel-detail/common/dates';
import { Placeholder } from '@lmn/design-system';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import getSearchParams from '@/app/components/search/common/getSearchParams';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import { URLParameters } from '@/app/common/urlParameters';
import { TextBase } from '@/app/components/hotel-detail/common/text';
import { useUrlSearchParams } from './UrlSearchParamsProvider';

type CheckInCheckOutWidgetProps = {
  checkinInformation?: CheckInformation;
  checkoutInformation?: CheckInformation;
  // True while the static response is in flight. False means it has arrived, so missing times are
  // genuinely absent and the widget renders nothing rather than a skeleton. Required: defaulting
  // it either way silently picks one of the two bugs for a caller who forgets it.
  isLoading: boolean;
} & HTMLAttributes<HTMLElement>;

// #region styled components
const Container = styled.div`
  ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
  border: 1px solid ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};
  border-radius: 8px;
  padding: 16px;
  width: 100%;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  height: fit-content;

  @media ${customCss.breakpoints.xl} {
    display: inline-flex;
  }
`;

const CheckInCheckOut = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex: 1;
  flex-direction: column;
`;

const Divider = styled.div`
  width: 1px;
  background-color: ${({ theme }) => theme.kallisto.color.border.static.borderDisabled};
`;

const Text = styled(TextBase).attrs({ variant: 'Title5' })`
  display: block;
  min-width: 90px;
  margin-bottom: 4px;
`;

// DateTime spans are intentionally font-less so CheckTime can inherit its parent's size (Body in
// the Evo hero/modal). CheckDateTime defaults to BodySmall on its non-Evo surfaces (the "good to
// know" widget, transport title), so establish the size on the wrapper the spans inherit from; the
// Evo policies modal opts into Body via the `variant` prop.
const Date = styled.div<{ $variant: 'Body' | 'BodySmall' }>`
  ${({ theme, $variant }) => theme.kallisto.mixins.font($variant)};
`;

const DateTime = styled.span`
  white-space: nowrap;
`;

const InlinePlaceholder = styled.span`
  display: inline-block;
  vertical-align: middle;
  width: 80%;
  height: 16px;
  background: ${({ theme }) => theme.kallisto.color.background.static.backgroundNeutralSubtle};
  border-radius: ${({ theme }) => theme.kallisto.border.radius.radius50};
`;
// #endregion styled components

export function HotelCheckinCheckout(props: CheckInCheckOutWidgetProps) {
  const { checkinInformation, checkoutInformation, isLoading, ...rest } = props;
  const { checkin: urlCheckin, checkout: urlCheckout } = useUrlSearchParams();
  const { localization } = usePageWrapperData();
  const { offersData } = useOffersContext();
  const { t } = useTranslation();

  const { locale } = localization;
  const searchParams = getSearchParams();
  const initialParams = useInitialParams();

  const checkin = offersData?.checkin || urlCheckin;
  const checkout = offersData?.checkout || urlCheckout;

  const hotelDates =
    initialParams.isHotelStaticPage && !searchParams[URLParameters.TRIP_DATES]
      ? undefined
      : getHotelCheckinCheckoutDates(locale, checkin, checkout);

  const hasCheckin = hasCheckTimes(checkinInformation) || Boolean(hotelDates?.checkin);
  const hasCheckout = hasCheckTimes(checkoutInformation) || Boolean(hotelDates?.checkout);

  // Each side stands on its own: a hotel that publishes only a check-in time would otherwise get a
  // "Check-out" heading over a blank column, which is the same empty-titled block this widget is
  // being taught to avoid. With neither side to show, the box goes whole.
  const showCheckin = isLoading || hasCheckin;
  const showCheckout = isLoading || hasCheckout;

  if (!showCheckin && !showCheckout) return null;

  return (
    <Container {...rest}>
      {showCheckin && (
        <CheckInCheckOut>
          <Text dangerouslySetInnerHTML={{ __html: filterXSS(t('vacation.select.section.goodtoknow.checkin')) }} />
          <CheckDateTime
            from={checkinInformation?.from}
            to={checkinInformation?.to}
            date={hotelDates?.checkin}
            isLoading={isLoading}
          />
        </CheckInCheckOut>
      )}
      {showCheckin && showCheckout && <Divider />}
      {showCheckout && (
        <CheckInCheckOut>
          <Text dangerouslySetInnerHTML={{ __html: filterXSS(t('vacation.select.section.goodtoknow.checkout')) }} />
          <CheckDateTime
            from={checkoutInformation?.from}
            to={checkoutInformation?.to}
            date={hotelDates?.checkout}
            isLoading={isLoading}
          />
        </CheckInCheckOut>
      )}
    </Container>
  );
}

export function CheckDateTime(props: {
  from: string | undefined;
  to: string | undefined;
  date: string | undefined;
  variant?: 'Body' | 'BodySmall';
  // Optional here: the policies section and the transport title only ever pass values they already
  // have, so nothing to wait for means nothing to draw.
  isLoading?: boolean;
}) {
  const { from, to, date, variant = 'BodySmall', isLoading = false } = props;
  const { t } = useTranslation();
  const { localization } = usePageWrapperData();
  const { country } = localization;
  const { hour: hourFrom, min: minFrom } = formatCheckinTime(from, country);
  const { hour: hourTo, min: minTo } = formatCheckinTime(to, country);

  const label =
    from && to ? (
      <>
        {date ? (
          <>
            <DateTime>{date}</DateTime>,{' '}
          </>
        ) : undefined}
        <DateTime>{`${hourFrom}:${minFrom} - ${hourTo}:${minTo}`}</DateTime>
      </>
    ) : from ? (
      <>
        {date ? (
          <>
            <DateTime>{date}</DateTime>,{' '}
          </>
        ) : undefined}
        <DateTime>{t('vacation.select.section.goodtoknow.checkincheckoutfrom.time', { hourFrom, minFrom })}</DateTime>
      </>
    ) : to ? (
      <>
        {date ? (
          <>
            <DateTime>{date}</DateTime>,{' '}
          </>
        ) : undefined}
        <DateTime>{t('vacation.select.section.goodtoknow.checkincheckoutto.time', { hourTo, minTo })}</DateTime>
      </>
    ) : date ? (
      <DateTime>{date}</DateTime>
    ) : isLoading ? (
      <Placeholder height="20px" width="70%" />
    ) : null;

  // No wrapper when there is no label: an empty styled div still takes a line box, so callers can
  // put this straight into a row without pre-checking whether it will draw anything.
  return label ? <Date $variant={variant}>{label}</Date> : null;
}

// travel-api sends `{ from: '', to: '' }` for a hotel it holds no times for, and the memsearch
// fallback seeds the same empty shape, so the times alone cannot say whether the real response has
// landed — callers pair this with SWR's `isLoading`.
export function hasCheckTimes(checkInformation: CheckInformation | undefined) {
  return Boolean(checkInformation?.from || checkInformation?.to);
}

export function CheckTime(props: {
  from: string | undefined;
  to: string | undefined;
  // Same contract as CheckDateTime's: the placeholder is a loading affordance, so once the
  // response is in, absent times render nothing instead of a bar that never resolves (MPH-1007).
  isLoading?: boolean;
}) {
  const { from, to, isLoading = false } = props;
  const { t } = useTranslation();

  const hourFrom = from?.substring(0, 2) || '';
  const minFrom = from?.substring(3) || '';
  const hourTo = to?.substring(0, 2) || '';
  const minTo = to?.substring(3) || '';

  const label =
    from && to ? (
      <DateTime>{`${from} - ${to}`}</DateTime>
    ) : from ? (
      <DateTime>{t('vacation.select.section.goodtoknow.checkincheckoutfrom.time', { hourFrom, minFrom })}</DateTime>
    ) : to ? (
      <DateTime>{t('vacation.select.section.goodtoknow.checkincheckoutto.time', { hourTo, minTo })}</DateTime>
    ) : isLoading ? (
      <InlinePlaceholder />
    ) : null;

  return label;
}
